/**
 * Sound effects, synthesised with the Web Audio API so there are no asset
 * files to load. Short square and triangle blips suit the pixel art.
 *
 * The AudioContext is built at startup so the recorded samples can start
 * loading at once — made without a gesture it simply starts suspended, and
 * the first play resumes it, which does follow one. Volume is a device
 * preference rather than game progress, so it lives in its own storage key.
 */

const VOLUME_KEY = 'dinoclicker.volume';
const MUTE_KEY = 'dinoclicker.muted';

export type SoundName =
  | 'click'
  | 'buy'
  | 'upgrade'
  | 'achievement'
  | 'chime'
  | 'crack'
  | 'whiff'
  | 'falling'
  | 'thud'
  | 'clink'
  | 'crash';

export interface Sound {
  play(name: SoundName): void;
  /**
   * 0 to 1, where the slider sits. Kept apart from `muted` so that unmuting
   * returns to the level that was chosen rather than to full.
   */
  readonly volume: number;
  setVolume(value: number): void;
  readonly muted: boolean;
  setMuted(value: boolean): void;
  /**
   * Start fetching and decoding the recorded sounds (SAMPLES). Called when
   * meteors unlock, and at load for a player who has them: the only samples
   * are the meteor's fall, a quarter of a megabyte a new player would
   * otherwise download for a sound they cannot hear for hours. Safe to call
   * again.
   */
  preloadSamples(): void;
}

interface Note {
  /** Hz. */
  freq: number;
  /** Seconds after the sound starts. */
  at: number;
  /** Seconds. */
  dur: number;
  type?: OscillatorType;
  gain?: number;
  /**
   * Glide to this frequency across the note instead of holding `freq`. For
   * noise it sweeps the band rather than the pitch.
   */
  to?: number;
  /**
   * White noise through a sweeping bandpass rather than an oscillator — the
   * rush of air a rock makes, and the same idea as a chiptune noise channel.
   */
  noise?: boolean;
  /**
   * Swell to the peak near the end rather than hitting it and decaying, for
   * something approaching rather than something struck.
   */
  swell?: boolean;
  /**
   * Seconds to reach the peak. The default is near-instant, which is right
   * for something struck; a longer one takes the stab off a sound that fires
   * over and over.
   */
  attack?: number;
  /**
   * How narrow the noise band is. Higher is tighter and more pitched; lower
   * is broader and more like a rush of air. Ignored by the oscillators.
   */
  q?: number;
}

/** Each effect is a tiny score. Frequencies are rough chiptune pitches. */
const SCORES: Readonly<Record<SoundName, readonly Note[]>> = {
  // The one sound that fires thousands of times a session, so it is the one
  // that has to stay pleasant. Noise rather than an oscillator: a tap is two
  // things meeting, and anything with a pitch you could hum reads as a note
  // instead — which is what the sine blip before this one did. A low, broad
  // band over 45ms lands it nearer a touch than a knock.
  click: [{ freq: 500, at: 0, dur: 0.045, noise: true, gain: 0.08, q: 1.6, attack: 0.004 }],
  buy: [
    { freq: 523, at: 0, dur: 0.07, type: 'square' },
    { freq: 784, at: 0.07, dur: 0.1, type: 'square' },
  ],
  upgrade: [
    { freq: 659, at: 0, dur: 0.06, type: 'square' },
    { freq: 880, at: 0.06, dur: 0.06, type: 'square' },
    { freq: 1319, at: 0.12, dur: 0.12, type: 'square' },
  ],
  achievement: [
    { freq: 523, at: 0, dur: 0.08, type: 'triangle' },
    { freq: 659, at: 0.08, dur: 0.08, type: 'triangle' },
    { freq: 784, at: 0.16, dur: 0.08, type: 'triangle' },
    { freq: 1047, at: 0.24, dur: 0.2, type: 'triangle' },
  ],
  chime: [
    { freq: 1568, at: 0, dur: 0.25, type: 'sine', gain: 0.1 },
    { freq: 2093, at: 0.12, dur: 0.35, type: 'sine', gain: 0.08 },
  ],
  crack: [
    { freq: 220, at: 0, dur: 0.04, type: 'square' },
    { freq: 1047, at: 0.04, dur: 0.06, type: 'square' },
    { freq: 1319, at: 0.1, dur: 0.06, type: 'square' },
    { freq: 1760, at: 0.16, dur: 0.16, type: 'square' },
  ],
  whiff: [
    { freq: 392, at: 0, dur: 0.08, type: 'square', gain: 0.1 },
    { freq: 262, at: 0.08, dur: 0.14, type: 'square', gain: 0.1 },
  ],
  // Meteors: the fall, the landing thud, a chip off the rock, and it bursting.
  // The fall runs just under the one second of the incoming animation, so the
  // rush finishes into the thud rather than over it.
  falling: [
    { freq: 3000, to: 260, at: 0, dur: 0.92, noise: true, gain: 0.16, swell: true },
    { freq: 1100, to: 150, at: 0.06, dur: 0.86, type: 'sine', gain: 0.06, swell: true },
  ],
  thud: [
    { freq: 110, at: 0, dur: 0.12, type: 'triangle', gain: 0.2 },
    { freq: 65, at: 0.03, dur: 0.2, type: 'sine', gain: 0.18 },
  ],
  clink: [
    { freq: 1975, at: 0, dur: 0.03, type: 'square', gain: 0.08 },
    { freq: 2637, at: 0.02, dur: 0.05, type: 'triangle', gain: 0.08 },
  ],
  crash: [
    { freq: 147, at: 0, dur: 0.08, type: 'square', gain: 0.16 },
    { freq: 98, at: 0.05, dur: 0.16, type: 'triangle', gain: 0.16 },
    { freq: 1568, at: 0.08, dur: 0.08, type: 'square', gain: 0.08 },
    { freq: 2093, at: 0.14, dur: 0.14, type: 'triangle', gain: 0.08 },
  ],
};

/**
 * Recorded sounds, which take over from the synthesised score of the same
 * name. A name with no files here stays synthesised, so the two can live side
 * by side and sounds can be replaced one at a time; a file that fails to load
 * or decode falls back to its score rather than going silent.
 *
 * More than one file is a set of takes, and one is picked at random each time
 * the sound fires. A meteor falls every few minutes for an entire run, and a
 * single recording of that becomes furniture; four do not.
 *
 * The takes keep the channels they were recorded with. Web Audio plays a
 * stereo buffer as stereo, and one of these has real width in it.
 *
 * They are stored at 16 kHz. decodeAudioData resamples to whatever rate the
 * context runs at, so nothing here has to know; the rate is a download-size
 * decision, and these are low rumbles with nothing above a few hundred Hz to
 * lose. It cut the set from 750 KB to 250 KB.
 */
const SAMPLES: Partial<Record<SoundName, readonly string[]>> = {
  falling: [
    'meteor-falling-1.wav',
    'meteor-falling-2.wav',
    'meteor-falling-3.wav',
    'meteor-falling-4.wav',
  ],
};

/** Level a sample plays at, against the synth's own gains. */
const SAMPLE_GAIN = 0.55;

const DEFAULT_GAIN = 0.15;
/** Seconds to the peak, for a note that does not ask for its own attack. */
const DEFAULT_ATTACK = 0.005;
/** How narrow a noise band is by default. */
const DEFAULT_Q = 1.4;

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Preference just won't persist.
  }
}

function readVolume(): number {
  const stored = read(VOLUME_KEY);
  return stored === null ? 1 : clampVolume(Number(stored));
}

export function createSound(): Sound {
  let volume = readVolume();
  /** Every take that has decoded, in whatever order they arrived. */
  const decoded = new Map<SoundName, AudioBuffer[]>();
  /** Files already asked for, so a broken one is not fetched on every play. */
  const requested = new Set<string>();
  // The same key the on/off toggle used, so anyone who muted before there was
  // a slider is still muted, at whatever level they had.
  let muted = read(MUTE_KEY) === '1';
  let ctx: AudioContext | null = null;

  function context(): AudioContext | null {
    if (ctx) return ctx;
    try {
      ctx = new AudioContext();
    } catch {
      ctx = null; // No Web Audio; the game is silent.
    }
    return ctx;
  }

  /** One second of white noise, made once and reused by every noise note. */
  let noise: AudioBuffer | null = null;
  function noiseBuffer(ac: AudioContext): AudioBuffer {
    if (noise) return noise;
    const buf = ac.createBuffer(1, Math.floor(ac.sampleRate), ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noise = buf;
    return buf;
  }

  /** What is actually heard. Mute wins over the slider without moving it. */
  function level(): number {
    return muted ? 0 : volume;
  }

  /**
   * Fetch and decode every sample ahead of its first use (preloadSamples).
   *
   * Loading on first use was worse than it sounds: the first play of a sound
   * is the one that starts the load, so it is always the synth that plays it.
   * A meteor falls every few minutes, so "always the synth on the first one"
   * meant hearing the old sound on every page load.
   *
   * Nothing waits on this. Anything not decoded yet still falls back to its
   * score, which is what a slow connection or a missing file gets.
   */
  function loadSamples(ac: AudioContext): void {
    for (const [key, files] of Object.entries(SAMPLES)) {
      const name = key as SoundName;
      for (const file of files ?? []) {
        if (requested.has(file)) continue;
        requested.add(file);
        void fetch(`${import.meta.env.BASE_URL}audio/${file}`)
          .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
          .then((buf) => ac.decodeAudioData(buf))
          .then((audio) => {
            const takes = decoded.get(name);
            if (takes) takes.push(audio);
            else decoded.set(name, [audio]);
          })
          .catch(() => {
            // Stays in `requested`, so a broken file is asked for once. The
            // rest of the set still plays; only if none arrive does the score.
          });
      }
    }
  }

  function playSample(ac: AudioContext, buffer: AudioBuffer): void {
    const gain = ac.createGain();
    gain.gain.value = SAMPLE_GAIN * level();
    gain.connect(ac.destination);
    const src = ac.createBufferSource();
    src.buffer = buffer;
    src.connect(gain);
    src.start();
  }

  function playNote(ac: AudioContext, note: Note): void {
    const gain = ac.createGain();
    const start = ac.currentTime + note.at;
    const peak = (note.gain ?? DEFAULT_GAIN) * level();
    const end = start + note.dur;

    // A short attack and an exponential release keep the blips from clicking;
    // a swelling note instead rises most of the way and drops at the very end.
    gain.gain.setValueAtTime(0.0001, start);
    if (note.swell) {
      gain.gain.exponentialRampToValueAtTime(peak, start + note.dur * 0.88);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
    } else {
      gain.gain.linearRampToValueAtTime(peak, start + (note.attack ?? DEFAULT_ATTACK));
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
    }
    gain.connect(ac.destination);

    if (note.noise) {
      const src = ac.createBufferSource();
      src.buffer = noiseBuffer(ac);
      src.loop = true;
      const band = ac.createBiquadFilter();
      band.type = 'bandpass';
      band.Q.value = note.q ?? DEFAULT_Q;
      band.frequency.setValueAtTime(note.freq, start);
      if (note.to !== undefined) band.frequency.exponentialRampToValueAtTime(note.to, end);
      src.connect(band).connect(gain);
      src.start(start);
      src.stop(end + 0.02);
      return;
    }

    const osc = ac.createOscillator();
    osc.type = note.type ?? 'square';
    osc.frequency.setValueAtTime(note.freq, start);
    if (note.to !== undefined) osc.frequency.exponentialRampToValueAtTime(note.to, end);
    osc.connect(gain);
    osc.start(start);
    osc.stop(end + 0.02);
  }

  // The context is made on the first sound, which follows a gesture, or when
  // the samples are preloaded; one made without a gesture starts suspended,
  // and `play` resumes it.
  return {
    preloadSamples() {
      const ac = context();
      if (ac) loadSamples(ac);
    },
    get volume() {
      return volume;
    },
    setVolume(value) {
      volume = clampVolume(value);
      write(VOLUME_KEY, String(volume));
    },
    get muted() {
      return muted;
    },
    setMuted(value) {
      muted = value;
      write(MUTE_KEY, value ? '1' : '0');
    },
    play(name) {
      if (level() <= 0) return;
      const ac = context();
      if (!ac) return;
      if (ac.state === 'suspended') {
        void ac.resume();
        // Browsers hold audio until the player first clicks or taps. Sounds
        // asked for before then (a meteor falling, an achievement) were
        // scheduled on a stopped clock and all burst out on that first click,
        // so skip them. A sound asked for by the gesture itself still plays.
        const activation = (navigator as { userActivation?: { isActive: boolean } }).userActivation;
        if (activation && !activation.isActive) return;
      }
      // Whichever takes have arrived; a set half loaded still picks from
      // what it has rather than waiting for the rest.
      const takes = decoded.get(name);
      if (takes && takes.length > 0) {
        playSample(ac, takes[Math.floor(Math.random() * takes.length)]!);
        return;
      }
      for (const note of SCORES[name]) playNote(ac, note);
    },
  };
}
